import "./index.css";
import {
  initBv,
  bvApi,
  type BvSession,
  mountShell,
  statRow,
  dataTable,
  card,
  emptyState,
  pill,
  openModal,
  flash,
  fmtMoney,
  fmtDate,
  skeletonCard,
  h,
} from "./bv-init";

interface Stats {
  active: number;
  campaigns: number;
  raised: number;
  contributions: number;
}
interface Campaign {
  id: number;
  title: string;
  slug: string;
  kind: "goal" | "raffle";
  goal_amount: number;
  currency: string;
  description: string | null;
  status: "active" | "closed";
  winner_name: string | null;
  raised: number;
  entries: number;
  link: string;
}
interface Contribution {
  id: number;
  name: string | null;
  amount: number;
  source: string;
  order_ref: string | null;
  created_at: string;
}
interface Recent {
  name: string | null;
  amount: number;
  source: string;
  created_at: string;
  campaign: string;
  currency: string;
}

let session: BvSession;
let currency = "JMD";

boot();

async function boot() {
  try {
    session = await initBv();
  } catch (err) {
    renderFatal(err);
    return;
  }
  currency = session.merchant.currency_code || "JMD";
  mountShell({
    brandIcon: "ticket",
    brandLogo: "/logo.svg",
    title: "Campaigns & Raffles",
    subtitle: `${session.merchant.name || "Your store"} · community crowdfunding`,
    poweredBy: "Marketplace",
    tabs: [
      { id: "overview", label: "Overview", icon: "ticket", render: renderOverview },
      { id: "campaigns", label: "Campaigns", icon: "list", render: renderCampaigns },
    ],
  });
}

function renderFatal(err: unknown) {
  const root = document.getElementById("root")!;
  root.innerHTML = "";
  root.append(
    h(
      "div",
      { class: "bv-fatal" },
      h("strong", null, "Couldn't start"),
      h("p", { class: "bv-muted" }, (err as any)?.message || "No session token found."),
    ),
  );
}

function progressBar(raised: number, goal: number): HTMLElement {
  const pct = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
  const track = h("div", {
    style: { background: "var(--line)", borderRadius: "999px", height: "10px", overflow: "hidden", margin: "8px 0 4px" },
  });
  track.append(h("div", { style: { width: `${pct}%`, height: "100%", background: "var(--accent)" } }));
  return track;
}

async function renderOverview(host: HTMLElement) {
  host.innerHTML = "";
  host.append(skeletonCard());
  const data = await bvApi<{ stats: Stats; recent: Recent[] }>("/api/overview").catch(() => null);
  host.innerHTML = "";
  if (!data) {
    host.append(emptyState({ icon: "alert", title: "Couldn't load", text: "Please try again." }));
    return;
  }
  const s = data.stats;
  host.append(
    statRow([
      { k: "Active campaigns", v: String(s.active), icon: "ticket", tone: Number(s.active) > 0 ? "accent" : undefined },
      { k: "Raised · all time", v: fmtMoney(Number(s.raised), currency), icon: "wallet", tone: "ok" },
      { k: "Contributions", v: String(s.contributions), icon: "users" },
      { k: "Campaigns", v: String(s.campaigns), icon: "list" },
    ]),
  );

  host.append(
    card({
      title: "Run a campaign",
      action: h("button", { class: "primary", onClick: () => openNew(host) }, "New campaign"),
      body: h("p", { class: "bv-muted" }, "Create a funding goal or a raffle. Sweep in paid orders as entries, or collect pledges through a public link."),
    }),
  );

  host.append(
    card({
      title: "Recent contributions",
      body: data.recent.length
        ? dataTable<Recent>({
            columns: [
              { head: "When", cell: (r) => fmtDate(r.created_at) },
              { head: "Name", cell: (r) => r.name || "Anonymous" },
              { head: "Campaign", cell: (r) => r.campaign },
              { head: "Source", cell: (r) => pill(r.source, r.source === "order" ? "" : "ok") },
              { head: "Amount", num: true, cell: (r) => fmtMoney(Number(r.amount), r.currency || currency) },
            ],
            rows: data.recent,
          })
        : emptyState({ icon: "ticket", title: "No contributions yet", text: "Create a campaign and sweep in paid orders." }),
    }),
  );
}

async function renderCampaigns(host: HTMLElement) {
  host.innerHTML = "";
  const head = h(
    "div",
    { class: "bv-row", style: { justifyContent: "space-between", alignItems: "center", marginBottom: "12px" } },
    h("h2", { style: { margin: "0", fontSize: "1.05rem" } }, "Campaigns"),
    h("button", { class: "primary", onClick: () => openNew(host) }, "New campaign"),
  );
  const grid = h("div", { class: "bv-grid" });
  host.append(head, grid);
  grid.append(skeletonCard());
  const d = await bvApi<{ campaigns: Campaign[] }>("/api/campaigns").catch(() => ({ campaigns: [] as Campaign[] }));
  grid.innerHTML = "";
  if (!d.campaigns.length) {
    grid.append(emptyState({ icon: "ticket", title: "No campaigns yet", text: "Create your first campaign to get started." }));
    return;
  }
  for (const c of d.campaigns) {
    const body = h("div");
    body.append(
      h(
        "div",
        { class: "bv-row", style: { justifyContent: "space-between", alignItems: "center" } },
        h("strong", null, c.title),
        pill(c.kind === "raffle" ? "raffle" : "goal", c.status === "closed" ? "" : "accent"),
      ),
      h("div", { class: "bv-muted", style: { fontSize: "0.85rem", marginTop: "2px" } }, `${c.entries} ${c.kind === "raffle" ? "entries" : "contributions"} · ${c.status}`),
      h("div", { style: { fontSize: "1.3rem", fontWeight: "700", marginTop: "8px" } }, fmtMoney(Number(c.raised), c.currency || currency)),
    );
    if (Number(c.goal_amount) > 0) {
      body.append(progressBar(Number(c.raised), Number(c.goal_amount)), h("div", { class: "bv-muted", style: { fontSize: "0.8rem" } }, `goal ${fmtMoney(Number(c.goal_amount), c.currency || currency)}`));
    }
    if (c.winner_name) body.append(h("div", { style: { marginTop: "6px" } }, "🎉 Winner: ", h("strong", null, c.winner_name)));
    body.append(
      h(
        "div",
        { class: "bv-row", style: { marginTop: "12px" } },
        h("button", { class: "secondary", onClick: () => openCampaign(c.id, host) }, "Manage"),
      ),
    );
    grid.append(card({ body }));
  }
}

function openNew(host: HTMLElement) {
  const title = h("input", { type: "text", placeholder: "e.g. New community oven" }) as HTMLInputElement;
  const kind = h("select", null, h("option", { value: "goal" }, "Funding goal"), h("option", { value: "raffle" }, "Raffle")) as HTMLSelectElement;
  const goal = h("input", { type: "number", min: "0", step: "0.01", value: "0" }) as HTMLInputElement;
  const desc = h("input", { type: "text", placeholder: "Short description (optional)" }) as HTMLInputElement;
  let close = () => {};
  const handle = openModal({
    title: "New campaign",
    body: h("div", { class: "bv-stack" }, field("Title", title), field("Type", kind), field(`Goal amount (${currency}) — 0 for open`, goal), field("Description", desc)),
    actions: [
      { label: "Cancel" },
      {
        label: "Create",
        primary: true,
        onClick: () => {
          if (!title.value.trim()) {
            flash("Title is required", "error");
            return true;
          }
          bvApi<{ campaign: Campaign }>("/api/campaigns", {
            method: "POST",
            body: JSON.stringify({ title: title.value.trim(), kind: kind.value, goal_amount: +goal.value, description: desc.value.trim() || null }),
          })
            .then((r) => {
              close();
              flash("Campaign created", "success");
              openCampaign(r.campaign.id, host);
            })
            .catch((e) => flash(e?.message || "Failed", "error"));
          return true;
        },
      },
    ],
  });
  close = handle.close;
}

async function openCampaign(id: number, host: HTMLElement) {
  const data = await bvApi<{ campaign: Campaign; contributions: Contribution[]; progress: { raised: number; contributions: number; contributors: number } }>(
    `/api/campaigns/${id}`,
  ).catch(() => null);
  if (!data) return flash("Couldn't load campaign", "error");
  const c = data.campaign;
  const body = h("div", { class: "bv-stack" });
  body.append(
    h(
      "div",
      { class: "bv-row", style: { justifyContent: "space-between", alignItems: "center" } },
      h("strong", null, c.title),
      pill(c.status, c.status === "closed" ? "" : "accent"),
    ),
    h("div", { style: { fontSize: "1.4rem", fontWeight: "700" } }, fmtMoney(Number(data.progress.raised), c.currency || currency)),
    h("div", { class: "bv-muted" }, `${data.progress.contributions} ${c.kind === "raffle" ? "entries" : "contributions"} · ${data.progress.contributors} people`),
  );
  if (Number(c.goal_amount) > 0) body.append(progressBar(Number(data.progress.raised), Number(c.goal_amount)));
  if (c.winner_name) body.append(h("div", null, "🎉 Winner: ", h("strong", null, c.winner_name)));

  // action row
  let close = () => {};
  const reopen = () => {
    close();
    openCampaign(id, host);
  };
  const actions = h("div", { class: "bv-row", style: { flexWrap: "wrap", gap: "8px" } });
  actions.append(
    h("button", { class: "secondary", onClick: () => sweep(id, reopen) }, "Sweep paid orders"),
    h("button", { class: "ghost", onClick: () => copyLink(c.link) }, "Copy link"),
  );
  if (c.kind === "raffle" && data.contributions.length)
    actions.append(h("button", { class: "secondary", onClick: () => draw(id, reopen) }, "Draw winner"));
  if (c.status === "active") actions.append(h("button", { class: "danger", onClick: () => closeCampaign(id, reopen) }, "Close"));
  body.append(actions);

  body.append(
    data.contributions.length
      ? dataTable<Contribution>({
          columns: [
            { head: "When", cell: (r) => fmtDate(r.created_at) },
            { head: "Name", cell: (r) => r.name || "Anonymous" },
            { head: "Source", cell: (r) => pill(r.source, r.source === "order" ? "" : "ok") },
            { head: "Amount", num: true, cell: (r) => fmtMoney(Number(r.amount), c.currency || currency) },
          ],
          rows: data.contributions,
        })
      : emptyState({ icon: "users", title: "No contributions yet", text: "Sweep paid orders or share the public link." }),
  );

  const handle = openModal({ title: "Campaign", body, actions: [{ label: "Done" }] });
  close = handle.close;
}

async function sweep(id: number, reopen: () => void) {
  const r = await bvApi<{ added: number }>(`/api/campaigns/${id}/sync`, { method: "POST" }).catch(() => null);
  if (!r) return flash("Sweep failed", "error");
  flash(r.added ? `Added ${r.added} entr${r.added === 1 ? "y" : "ies"} from paid orders.` : "No new paid orders.", r.added ? "success" : "info");
  if (r.added) reopen();
}

async function draw(id: number, reopen: () => void) {
  const r = await bvApi<{ winner: { name: string | null } }>(`/api/campaigns/${id}/draw`, { method: "POST" }).catch(() => null);
  if (!r) return flash("No entries to draw from", "error");
  flash(`🎉 Winner: ${r.winner.name || "Anonymous"}`, "success");
  reopen();
}

async function closeCampaign(id: number, reopen: () => void) {
  const ok = await bvApi(`/api/campaigns/${id}/close`, { method: "POST" }).then(() => true).catch(() => false);
  flash(ok ? "Campaign closed" : "Couldn't close", ok ? "success" : "error");
  if (ok) reopen();
}

function copyLink(link: string) {
  navigator.clipboard?.writeText(link).then(
    () => flash("Public link copied", "success"),
    () => flash(link, "info"),
  );
}

function field(label: string, input: HTMLElement): HTMLElement {
  return h("div", { class: "bv-field" }, h("label", { class: "bv-label" }, label), input);
}
